import React, { useState, useEffect } from 'react';
import { ref, remove, get, update } from 'firebase/database'; // Import des fonctions Firebase
import db from '../FireBase/firebase'; // Import de l'instance Firebase
import { fetchAllCollections } from '../FireBase/firebaseService'; // Importer la fonction pour récupérer les collections
import Swal from 'sweetalert2';
import CryptoJS from 'crypto-js'; // Import de crypto-js pour le hachage

let collectionActuelle = localStorage.getItem('collectionName');

function BdSettings() {
  const [collectionName, setCollectionName] = useState(
    localStorage.getItem('collectionName') || ''
  );
  const [collections, setCollections] = useState([]);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const handleTooltipToggle = () => {
    setTooltipVisible(!tooltipVisible);
  };

  useEffect(() => {
    // Récupérer les collections lors du montage du composant
    fetchAllCollections(
      (data) => {
        setCollections(data); // On met à jour le state avec les collections
      },
      (error) => {
        console.error('Erreur lors de la récupération des collections:', error);
      }
    );
  }, []);

  // Fonction pour changer le nom de la collection
  const handleCollectionNameChange = (e) => {
    const newName = e.target.value;
    setCollectionName(newName);
    localStorage.setItem('collectionName', newName); // Stocke la valeur dans la session
  };

  // Supprimer toutes les données de la collection
  const handleDeleteData = async () => {
    if (!collectionName) {
      alert('Veuillez spécifier un nom de collection.');
      return;
    }

    const mdpRef = ref(db, 'parametres/mdp');
    const snapshot = await get(mdpRef);
    const mdp = snapshot.val();
    
    const password = await demandeMDP();
    if (!password) {
      return;
    }
    
    const hashedPassword = CryptoJS.SHA256(password).toString(CryptoJS.enc.Base64);
    if (hashedPassword !== mdp.mdp) {
      Swal.fire('Mot de passe incorrect', 'Le mot de passe entré est incorrect.', 'error');
      return;
    }

    const saveJson = await Swal.fire({
      title: 'Sauvegarder les données ?',
      text: 'Avant de continuer, souhaitez-vous sauvegarder les données actuelles en format JSON ?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Oui',
      cancelButtonText: 'Non'
    });

    if (saveJson.isConfirmed) {
      await handleExportData();
    }

    const confirmDelete = await Swal.fire({
      title: 'Confirmation finale',
      html: `Etes-vous sûr de vouloir supprimer les données correspondantes au circuit <strong>${collectionName}</strong> ?<br></br><strong>Cette action est irréversible.</strong>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui',
      cancelButtonText: 'Annuler',
      customClass: {
        confirmButton: 'swal2-delete-confirm',
        cancelButton: 'swal2-delete-cancel'
      }
    });

    if (confirmDelete.isConfirmed) {
      const collectionRef = ref(db, collectionName);
      try {
        await remove(collectionRef);
        Swal.fire('Supprimé !', `Toutes les données du circuit <strong>${collectionName}</strong> ont été supprimées.`, 'success');
        collections.splice(collections.indexOf(collectionName), 1);
        setCollections([...collections]);    
        setCollectionName(collections[0]);
        localStorage.setItem('collectionName', collections[0]); // Stocke la valeur dans la session
      } catch (error) {
        console.error('Erreur lors de la suppression des données :', error);
        Swal.fire('Erreur', 'Une erreur est survenue lors de la suppression.', 'error');
      }
    }
  };

  // Exporter la BD en JSON
  const handleExportData = async () => {
    if (!collectionName) {
      Swal.fire('Erreur', `Veuillez spécifier un nom de collection.`, 'info');
      return;
    }

    const mdpRef = ref(db, 'parametres/mdp');
    const snapshot = await get(mdpRef);
    const mdp = snapshot.val();
    
    const password = await demandeMDP();
    if (!password) {
      return;
    }
    
    const hashedPassword = CryptoJS.SHA256(password).toString(CryptoJS.enc.Base64);
    if (hashedPassword !== mdp.mdp) {
      Swal.fire('Mot de passe incorrect', 'Le mot de passe entré est incorrect.', 'error');
      return;
    }
    const collectionRef = ref(db, collectionName);
    try {
      const snapshot = await get(collectionRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        const json = JSON.stringify(data, null, 2); // Convertir en JSON formaté
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${collectionName}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        Swal.fire('Téléchargement impossible', `Aucune donnée n'a été trouvée pour le circuit <strong>${collectionName}</strong>.`, 'error');
      }
    } catch (error) {
      console.error('Erreur lors de l\'exportation des données :', error);
      Swal.fire('Erreur', `Une erreur est survenue lors de l'exportation.`, 'error');
    }
  };

  async function demandeMDP() {
    // Afficher un message pour demander le mot de passe actuel
    const { value: password } = await Swal.fire({
      title: 'Entrez le mot de passe actuel',
      input: 'password',
      inputLabel: 'Mot de passe actuel',
      inputPlaceholder: 'Entrez votre mot de passe',
      inputAttributes: {
        autocapitalize: 'off',
        autocorrect: 'off'
      },
      showCancelButton: true,
      confirmButtonText: 'Valider',
      cancelButtonText: 'Annuler',
      inputValidator: (value) => {
        if (!value) {
          return 'Vous devez entrer un mot de passe.';
        }
      }
    });
    return password;
  }

  const handleUpdatePassword = async () => {
    // Récupérer les paramètres de la BD, y compris le mot de passe haché
    const mdpRef = ref(db, 'parametres/mdp');
    const snapshot = await get(mdpRef);
    const mdp = snapshot.val();
    
    const password = await demandeMDP();
    if (!password) {
      return;
    }
    
    // Vérifier si le mot de passe est correct
    const hashedPassword = CryptoJS.SHA256(password).toString(CryptoJS.enc.Base64); // Hacher le mot de passe entré
    if (hashedPassword === mdp.mdp) { // Comparer le mot de passe haché
      // Si le mot de passe est correct, demander le nouveau mot de passe
      const { value: newPassword } = await Swal.fire({
        title: 'Entrez le nouveau mot de passe',
        input: 'password',
        inputLabel: 'Nouveau mot de passe',
        inputPlaceholder: 'Entrez votre nouveau mot de passe',
        inputAttributes: {
          autocapitalize: 'off',
          autocorrect: 'off'
        },
        showCancelButton: true,
        confirmButtonText: 'Valider',
        cancelButtonText: 'Annuler',
        inputValidator: (value) => {
          if (!value) {
            return 'Vous devez entrer un mot de passe.';
          }
        }
      });
      
      // Vérifier si le nouveau mot de passe est valide
      if (newPassword) {
        // Hachage du nouveau mot de passe avant de le stocker
        const hashedNewPassword = CryptoJS.SHA256(newPassword).toString(CryptoJS.enc.Base64);
        
        // Mettre à jour le mot de passe dans la base de données
        try {
          await update(mdpRef, { mdp: hashedNewPassword });
          Swal.fire('Mot de passe modifié', 'Le mot de passe a été modifié avec succès.', 'success');
        } catch (error) {
          console.error('Erreur lors de la modification du mot de passe :', error);
          Swal.fire('Erreur', 'Une erreur est survenue lors de la modification du mot de passe.', 'error');
        }
      }
    } else {
      Swal.fire('Mot de passe incorrect', 'Le mot de passe entré est incorrect.', 'error');
    }
  };
  

  return (
    <div>
      <h3>Paramètres de la Base de Données</h3>
      <label>
        Sélectionner le circuit
        <select
          value={collectionName}
          onChange={handleCollectionNameChange}
          id="changementCollection"
          title="Sélectionner un circuit"
        >
          {collections.map((collection, index) => (
            <option key={index} value={collection}>
              {collection}
            </option>
          ))}
        </select>
        <button onClick={handleTooltipToggle} title="Sélectionnez un circuit pour afficher les données correspondantes. Les circuits disponibles sont ceux choisis dans le code Arduino." id="boutonInfos">i</button>
        {tooltipVisible && (
          <div className="tooltip">
            Sélectionnez un circuit pour afficher les données correspondantes.<br></br>Les circuits disponibles sont ceux choisis dans le code Arduino.<br></br>Les affichages seront automatiquement mis à jour à la fermeture des paramètres.
          </div>
        )}
      </label>
      <br />
      <br />
      <button onClick={handleDeleteData} id="deleteDB">Supprimer les données</button>
      <br />
      <button onClick={handleExportData} id="exportToJSON">Exporter en JSON</button>
      <br />
      <button onClick={handleUpdatePassword} id="modifMDP">Modifier le mot de passe</button>
    </div>
  );
}

export default BdSettings;
export { collectionActuelle };
